import { Relation, Thing } from '@pod-os/core';
import { Component, Element, Event, EventEmitter, h, Prop, State } from '@stencil/core';
import { ResourceAware, ResourceEventEmitter, subscribeResource } from '../events/ResourceAware';

/**
 * Renders a link to a resource. The resource is either identified by the given `uri` property, by the resource in
 * context (given by a surrounding `pos-resource` element), or by following a relation from or to the resource in
 * context (`rel` / `rev` attributes).
 *
 * By default, it renders a label and description of the resource. You can override this by providing custom content as
 * child elements.
 *
 * **Important:** This component will fire a `pod-os:link` event when clicked instead of doing a normal browser navigation.
 * To actually navigate to the link target, you have to listen to the event and then perform the navigation
 * programmatically. Other link-related behaviours (like open in a new tab and copy link target) are working normally.
 * This is to support client-side navigation.
 */
@Component({
  tag: 'pos-rich-link',
  shadow: true,
  styleUrl: 'pos-rich-link.css',
})
export class PosRichLink implements ResourceAware {
  /**
   * Link will use this URI
   */
  @Prop() uri?: string;
  /**
   * Link will be obtained by following the predicate with this URI forward from a resource
   */
  @Prop() rel?: string;
  /**
   * Link will be obtained by following the predicate with this URI in reverse from a resource
   */
  @Prop() rev?: string;

  @Event({ eventName: 'pod-os:link' }) linkEmitter: EventEmitter;

  @Event({ eventName: 'pod-os:resource' })
  subscribeResource: ResourceEventEmitter;

  @Event({ eventName: 'pod-os:error' }) errorEmitter: EventEmitter;

  @State() link?: string;
  @State() followPredicate: boolean = false;
  @State() error: string = null;

  @Element() host: HTMLElement;

  @State()
  private showCustomContent: boolean = false;

  componentWillLoad() {
    this.showCustomContent = // custom content can either be given as child elements, or as just text, so we check for both:
      !!this.host.lastElementChild || // either has any child element
      this.host.textContent.trim() != ''; // or contains a non-empty text
    if (!this.uri) subscribeResource(this);
  }

  receiveResource = (resource: Thing) => {
    const addLink = (links: Relation[], resource: Thing, predicate: string, direction: string) => {
      if (links.length == 0) {
        this.error = 'No matching link found';
        this.errorEmitter.emit(
          new Error(`pos-rich-link: No matching link found from ${resource.uri} ${direction}=${predicate}`),
        );
      } else if (links[0].uris.length > 1) {
        this.error = 'More than one matching link found';
        this.errorEmitter.emit(
          new Error(`pos-rich-link: More than one matching link found from ${resource.uri} ${direction}=${predicate}`),
        );
      } else {
        this.link = links[0].uris[0];
        this.followPredicate = true;
      }
    };

    if (this.rel) {
      addLink(resource.relations(this.rel), resource, this.rel, 'rel');
    } else if (this.rev) {
      addLink(resource.reverseRelations(this.rev), resource, this.rev, 'rev');
    } else {
      this.link = resource.uri;
    }
  };

  render() {
    const content = (uri: string) =>
      this.showCustomContent ? (
        <a
          href={uri}
          onClick={e => {
            e.preventDefault();
            this.linkEmitter.emit(uri);
          }}
        >
          <slot></slot>
        </a>
      ) : (
        <p class="content">
          <a
            href={uri}
            onClick={e => {
              e.preventDefault();
              this.linkEmitter.emit(uri);
            }}
          >
            <pos-label />
          </a>
          <span class="url">{new URL(uri).host}</span>
          <pos-description />
        </p>
      );

    if (this.error) {
      return this.error;
    } else if (this.followPredicate) {
      return (
        <pos-resource lazy={true} uri={this.link}>
          {content(this.link)}
        </pos-resource>
      );
    } else if (this.link) {
      return content(this.link);
    } else if (this.uri) {
      return (
        <pos-resource lazy={true} uri={this.uri}>
          {content(this.uri)}
        </pos-resource>
      );
    } else {
      return null;
    }
  }
}
